import { Component, Input, ViewChild, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { EnfafSelectLightComponent } from '../../../../components/enfaf-select-light/enfaf-select-light.component';
import { EnfafIconComponent } from '../../../../components/enfaf-icon/enfaf-icon.component';
import { EventSegmentationTypeOption } from '../events-form/events-form.constants';
import { MultiAutocompleteLightComponent, SelectOption } from '../../../../components/multi-autocomplete-light/multi-autocomplete-light.component';
import { ApiService } from '../../../../core/services/api/api.service';
import { Course } from '../../../../models/interfaces/course.interface';
import { EventSegmentationType } from '../../../../models/enums/event-segmentation-type.enum';

@Component({
  selector: 'app-event-management',
  imports: [ReactiveFormsModule, EnfafSelectLightComponent, EnfafIconComponent, MultiAutocompleteLightComponent],
  templateUrl: './event-management.component.html',
  styleUrls: ['./event-management.component.scss']
})
export class EventManagementComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() segmentationControl?: FormControl<EventSegmentationType | null>;
  @Input() segmentationTypeOptions: EventSegmentationTypeOption[] = [];
  @ViewChild('registeredCoursesAutocomplete') registeredCoursesAutocomplete?: MultiAutocompleteLightComponent;

  EventSegmentationType = EventSegmentationType;
  selectedRegisteredCourses: SelectOption[] = [];

  private coursesCache: Course[] = [];
  private coursesFetchPromise: Promise<any> | null = null;
  private segmentationSubscription?: Subscription;
  private syncInterval?: any;
  private isComponentDestroyed = false;

  constructor(
    private api: ApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (this.segmentationControl) {
      this.segmentationSubscription = this.segmentationControl.valueChanges.subscribe((value) => {
        if (this.registeredCoursesAutocomplete) {
          this.registeredCoursesAutocomplete.resetSelection();
        }

        this.selectedRegisteredCourses = [];
      });
    }
  }

  ngAfterViewInit() {
    this.syncInterval = setInterval(() => {
      if (this.segmentationControl?.value === EventSegmentationType.RegisteredStudentsOnly) {
        if (this.registeredCoursesAutocomplete) {
          const currentRegistered = this.registeredCoursesAutocomplete.selected;
          if (JSON.stringify(this.selectedRegisteredCourses) !== JSON.stringify(currentRegistered)) {
            this.selectedRegisteredCourses = [...currentRegistered];
            this.cdr.detectChanges();
          }
        }
      } else {
        if (this.selectedRegisteredCourses.length > 0) {
          this.selectedRegisteredCourses = [];
          this.cdr.detectChanges();
        }
      }
    }, 200);
  }

  ngOnDestroy() {
    this.isComponentDestroyed = true;
    if (this.segmentationSubscription) {
      this.segmentationSubscription.unsubscribe();
    }
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
  }

  removeCourse(course: SelectOption) {
    if (this.registeredCoursesAutocomplete) {
      this.registeredCoursesAutocomplete.removeSelected(course);
      this.selectedRegisteredCourses = this.registeredCoursesAutocomplete.selected;
    }
  }

  fetchCourses = (search: string, page: number, pageSize: number): Observable<{ data: SelectOption[]; total: number }> => {
    return new Observable((observer) => {
      this.getCourses()
        .then((courses) => {
          this.processCachedCourses(courses, search, page, pageSize, observer);
        })
        .catch((error) => {
          if (this.isComponentDestroyed) {
            observer.complete();
            return;
          }
          console.error('Error fetching courses:', error);
          observer.error(error);
        });
    });
  };

  private async getCourses(): Promise<Course[]> {
    if (this.coursesCache.length > 0) {
      return this.coursesCache;
    }

    if (this.coursesFetchPromise) {
      const response = await this.coursesFetchPromise;
      this.coursesCache = response.courses || response || [];
      return this.coursesCache;
    }

    this.coursesFetchPromise = firstValueFrom(this.api.fetchAllCourses());

    try {
      const response = await this.coursesFetchPromise;
      this.coursesCache = response.courses || response || [];
      this.coursesFetchPromise = null;
      return this.coursesCache;
    } catch (error) {
      this.coursesFetchPromise = null;
      throw error;
    }
  }

  private processCachedCourses(courses: Course[], search: string, page: number, pageSize: number, observer: any) {
    const filteredCourses = search ? courses.filter((course) => course.name.toLowerCase().includes(search.toLowerCase())) : courses;

    const startIndex = page * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filteredCourses.length);
    const paginatedCourses = filteredCourses.slice(startIndex, endIndex);

    const mappedData: SelectOption[] = paginatedCourses.map((course: Course) => ({
      id: course.courseId,
      title: course.name,
      value: course.courseId
    }));

    observer.next({
      data: mappedData,
      total: filteredCourses.length
    });
    observer.complete();
  }

  getSelectedCourses(): Course[] {
    if (this.coursesCache.length === 0) return [];

    return this.selectedRegisteredCourses
      .map((selected) => this.coursesCache.find((course) => course.courseId.toString() === selected.id))
      .filter((course) => course !== undefined) as Course[];
  }

  setSelectedCourses(courses: Course[]) {
    const options: SelectOption[] = courses.map((course) => ({
      id: course.courseId.toString(),
      title: course.name || 'Curso sin nombre',
      value: course.courseId.toString()
    }));

    this.selectedRegisteredCourses = options;

    if (this.registeredCoursesAutocomplete) {
      // En lugar de setSelected, simplemente asignamos al array selected
      this.registeredCoursesAutocomplete.selected = [...options];
    }
  }
}
